import { useState, useEffect } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  Calendar,
  MapPin,
  Info,
  ExternalLink,
  Zap,
  Car,
  Home,
  ShoppingCart,
  Briefcase
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, ComposedChart, Area, AreaChart } from 'recharts';

export const InflationEvolutionPage = () => {
  const { t, i18n } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState(i18n.language === 'es' ? 'ES' : 'FR');
  const [timeframe, setTimeframe] = useState('12months');
  const [comparisonMode, setComparisonMode] = useState('categories');

  useEffect(() => {
    setSelectedCountry(i18n.language === 'es' ? 'ES' : 'FR');
  }, [i18n.language]);

  // Generate inflation data (simulated - in real app would use INSEE/INE APIs)
  const generateInflationData = (country, months) => {
    const baseInflation = {
      FR: { general: 4.8, food: 12.2, energy: 15.8, transport: 6.3, housing: 3.2, services: 2.9 },
      ES: { general: 5.8, food: 13.8, energy: 18.2, transport: 7.8, housing: 4.1, services: 3.4 }
    };

    const data = [];
    const now = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthProgress = i / months;
      
      const general = baseInflation[country].general * (0.8 + Math.random() * 0.4);
      const food = baseInflation[country].food * (0.9 + Math.random() * 0.2);
      const energy = baseInflation[country].energy * (0.7 + Math.random() * 0.6);
      const transport = baseInflation[country].transport * (0.8 + Math.random() * 0.4);
      const housing = baseInflation[country].housing * (0.9 + Math.random() * 0.2);
      const services = baseInflation[country].services * (0.8 + Math.random() * 0.4);
      
      data.push({
        month: date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'fr-FR', { 
          month: 'short', 
          year: '2-digit' 
        }),
        date,
        general: Number(general.toFixed(1)),
        food: Number(food.toFixed(1)),
        energy: Number(energy.toFixed(1)),
        transport: Number(transport.toFixed(1)),
        housing: Number(housing.toFixed(1)),
        services: Number(services.toFixed(1))
      });
    }

    return data;
  };

  const inflationData = generateInflationData(selectedCountry, timeframe === '6months' ? 6 : timeframe === '12months' ? 12 : 24);

  const categories = [
    {
      key: 'general',
      name: i18n.language === 'es' ? 'Índice General' : 'Indice Général',
      icon: BarChart3,
      color: '#6366f1',
      target: 2.0
    },
    {
      key: 'food',
      name: i18n.language === 'es' ? 'Alimentación' : 'Alimentation',
      icon: ShoppingCart,
      color: '#10b981',
      target: 3.0
    },
    {
      key: 'energy',
      name: i18n.language === 'es' ? 'Energía' : 'Énergie',
      icon: Zap,
      color: '#f59e0b',
      target: 5.0
    },
    {
      key: 'transport',
      name: i18n.language === 'es' ? 'Transporte' : 'Transport',
      icon: Car,
      color: '#3b82f6',
      target: 3.0
    },
    {
      key: 'housing',
      name: i18n.language === 'es' ? 'Vivienda' : 'Logement',
      icon: Home,
      color: '#ef4444',
      target: 2.5
    },
    {
      key: 'services',
      name: i18n.language === 'es' ? 'Servicios' : 'Services',
      icon: Briefcase,
      color: '#8b5cf6',
      target: 2.0
    }
  ];

  const getCurrentInflation = (categoryKey) => {
    const latest = inflationData[inflationData.length - 1];
    return latest ? latest[categoryKey] : 0;
  };

  const getInflationTrend = (categoryKey) => {
    if (inflationData.length < 2) return 0;
    const latest = inflationData[inflationData.length - 1];
    const previous = inflationData[inflationData.length - 2];
    return latest[categoryKey] - previous[categoryKey];
  };

  const getInflationStatus = (current, target) => {
    if (current <= target) return 'good';
    if (current <= target * 2) return 'moderate';
    return 'high';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'bg-green-100 text-green-800 border-green-200';
      case 'moderate': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    if (i18n.language === 'es') {
      switch (status) {
        case 'good': return 'Controlado';
        case 'moderate': return 'Moderado';
        case 'high': return 'Alto';
        default: return 'Normal';
      }
    } else {
      switch (status) {
        case 'good': return 'Maîtrisé';
        case 'moderate': return 'Modéré';
        case 'high': return 'Élevé';
        default: return 'Normal';
      }
    }
  };

  const europeanComparison = [
    { country: i18n.language === 'es' ? 'España' : 'Espagne', flag: '🇪🇸', inflation: 5.8, trend: 0.2 },
    { country: 'France', flag: '🇫🇷', inflation: 4.8, trend: -0.1 },
    { country: i18n.language === 'es' ? 'Alemania' : 'Allemagne', flag: '🇩🇪', inflation: 6.1, trend: 0.3 },
    { country: i18n.language === 'es' ? 'Italia' : 'Italie', flag: '🇮🇹', inflation: 7.2, trend: 0.5 },
    { country: i18n.language === 'es' ? 'Países Bajos' : 'Pays-Bas', flag: '🇳🇱', inflation: 3.2, trend: -0.3 },
    { country: i18n.language === 'es' ? 'Media UE' : 'Moyenne UE', flag: '🇪🇺', inflation: 5.4, trend: 0.1 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="inflation-page-title"
            >
              {t('tools.inflation.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {i18n.language === 'es'
                ? 'Analiza la evolución de la inflación en tiempo real y su impacto en tu poder adquisitivo. Datos oficiales actualizados mensualmente.'
                : 'Analysez l\'évolution de l\'inflation en temps réel et son impact sur votre pouvoir d\'achat. Données officielles mises à jour mensuellement.'
              }
            </p>
          </div>

          {/* Controls */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <MapPin className="w-4 h-4 mr-2 text-sage-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FR">🇫🇷 France</SelectItem>
                  <SelectItem value="ES">🇪🇸 España</SelectItem>
                </SelectContent>
              </Select>

              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <Calendar className="w-4 h-4 mr-2 text-sage-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6months">
                    {i18n.language === 'es' ? '6 meses' : '6 mois'}
                  </SelectItem>
                  <SelectItem value="12months">
                    {i18n.language === 'es' ? '12 meses' : '12 mois'}
                  </SelectItem>
                  <SelectItem value="24months">
                    {i18n.language === 'es' ? '24 meses' : '24 mois'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Summary Dashboard */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {categories.map((category) => {
              const currentRate = getCurrentInflation(category.key);
              const trend = getInflationTrend(category.key);
              const status = getInflationStatus(currentRate, category.target);
              const IconComponent = category.icon;

              return (
                <Card key={category.key} className="hover:shadow-lg transition-all duration-200">
                  <CardContent className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-lg flex items-center justify-center" style={{ backgroundColor: category.color + '20' }}>
                      <IconComponent className="w-5 h-5" style={{ color: category.color }} />
                    </div>
                    
                    <div className="font-bold text-lg" style={{ color: category.color }}>
                      {currentRate.toFixed(1)}%
                    </div>
                    
                    <div className="text-xs text-gray-600 mb-2">
                      {category.name}
                    </div>
                    
                    <Badge className={getStatusColor(status)}>
                      {getStatusText(status)}
                    </Badge>
                    
                    <div className="flex items-center justify-center gap-1 mt-1">
                      {trend > 0 ? (
                        <TrendingUp className="w-3 h-3 text-red-500" />
                      ) : trend < 0 ? (
                        <TrendingDown className="w-3 h-3 text-green-500" />
                      ) : null}
                      <span className={`text-xs ${trend > 0 ? 'text-red-600' : trend < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                        {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Chart */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  {i18n.language === 'es' ? 'Evolución de la Inflación' : 'Évolution de l\'Inflation'}
                </div>
                <Badge variant="outline">
                  {selectedCountry === 'FR' ? '🇫🇷 INSEE' : '🇪🇸 INE'}
                </Badge>
              </CardTitle>
              <CardDescription>
                {i18n.language === 'es'
                  ? 'Tasa de variación anual de precios por categorías. Objetivo BCE: 2%'
                  : 'Taux de variation annuel des prix par catégories. Objectif BCE : 2%'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={inflationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={12}
                    label={{ 
                      value: '%', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, categories.find(c => c.key === name)?.name || name]}
                    labelStyle={{ color: '#374151' }}
                    contentStyle={{ 
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px'
                    }}
                  />
                  
                  {/* Target line at 2% */}
                  <Line 
                    type="monotone" 
                    dataKey={() => 2}
                    stroke="#9ca3af" 
                    strokeDasharray="5 5"
                    strokeWidth={1}
                    dot={false}
                    name={i18n.language === 'es' ? 'Objetivo BCE' : 'Objectif BCE'}
                  />
                  
                  {categories.map(category => (
                    <Line 
                      key={category.key}
                      type="monotone" 
                      dataKey={category.key}
                      stroke={category.color}
                      strokeWidth={2}
                      dot={{ fill: category.color, strokeWidth: 1, r: 3 }}
                      name={category.name}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* European Comparison */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                {i18n.language === 'es' ? 'Comparación Europea' : 'Comparaison Européenne'}
              </CardTitle>
              <CardDescription>
                {i18n.language === 'es' 
                  ? 'Inflación general por países (datos más recientes)'
                  : 'Inflation générale par pays (données les plus récentes)'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {europeanComparison
                  .sort((a, b) => a.inflation - b.inflation)
                  .map((country, index) => (
                    <div key={country.country} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="text-lg">{country.flag}</div>
                      <div className="flex-1">
                        <div className="font-medium text-sage-900">{country.country}</div>
                        <div className="text-sm text-sage-600">
                          {i18n.language === 'es' ? 'Tendencia mensual:' : 'Tendance mensuelle :'} 
                          <span className={country.trend >= 0 ? 'text-red-600' : 'text-green-600'}>
                            {country.trend > 0 ? '+' : ''}{country.trend.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${
                          country.inflation <= 2 ? 'text-green-600' :
                          country.inflation <= 4 ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {country.inflation.toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          #{index + 1}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Impact Calculator */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  {i18n.language === 'es' ? 'Impacto en tu Poder Adquisitivo' : 'Impact sur votre Pouvoir d\'Achat'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-6 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border border-red-200">
                  <div className="text-3xl font-bold text-red-700 mb-2">
                    -{getCurrentInflation('general').toFixed(1)}%
                  </div>
                  <div className="text-sm text-red-600">
                    {i18n.language === 'es' ? 'Pérdida de poder adquisitivo anual' : 'Perte de pouvoir d\'achat annuelle'}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700">
                    {i18n.language === 'es' ? 'Impacto por 1000€ de gastos anuales:' : 'Impact pour 1000€ de dépenses annuelles :'}
                  </div>
                  
                  {categories.slice(1).map(category => {
                    const rate = getCurrentInflation(category.key);
                    const impact = (rate / 100) * 1000;
                    
                    return (
                      <div key={category.key} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2">
                          <category.icon className="w-4 h-4" style={{ color: category.color }} />
                          <span className="text-sm">{category.name}</span>
                        </div>
                        <span className="font-medium text-red-600">+{impact.toFixed(0)}€</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  {i18n.language === 'es' ? 'Estrategias de Protección' : 'Stratégies de Protection'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {getCurrentInflation('energy') > 10 && (
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        {i18n.language === 'es' ? 'Energía (+' : 'Énergie (+'}
                        {getCurrentInflation('energy').toFixed(1)}%)
                      </h4>
                      <ul className="text-sm text-orange-700 space-y-1">
                        <li>• {i18n.language === 'es' ? 'Comparar proveedores de electricidad' : 'Comparer fournisseurs électricité'}</li>
                        <li>• {i18n.language === 'es' ? 'Considerar energía solar' : 'Considérer énergie solaire'}</li>
                        <li>• {i18n.language === 'es' ? 'Optimizar consumo doméstico' : 'Optimiser consommation domestique'}</li>
                      </ul>
                      <Button size="sm" asChild className="mt-2 bg-orange-600 hover:bg-orange-700">
                        <a href="/electricity">
                          {i18n.language === 'es' ? 'Ver comparador' : 'Voir comparateur'}
                        </a>
                      </Button>
                    </div>
                  )}

                  {getCurrentInflation('food') > 8 && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4" />
                        {i18n.language === 'es' ? 'Alimentación (+' : 'Alimentation (+'}
                        {getCurrentInflation('food').toFixed(1)}%)
                      </h4>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• {i18n.language === 'es' ? 'Planificar menús semanales' : 'Planifier menus hebdomadaires'}</li>
                        <li>• {i18n.language === 'es' ? 'Comprar productos estacionales' : 'Acheter produits de saison'}</li>
                        <li>• {i18n.language === 'es' ? 'Usar apps de descuentos' : 'Utiliser apps de réductions'}</li>
                      </ul>
                      <Button size="sm" asChild className="mt-2 bg-green-600 hover:bg-green-700">
                        <a href="/ebooks">
                          {i18n.language === 'es' ? 'Ver guías' : 'Voir guides'}
                        </a>
                      </Button>
                    </div>
                  )}

                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">
                      {i18n.language === 'es' ? 'Recomendación general' : 'Recommandation générale'}
                    </h4>
                    <p className="text-sm text-blue-700">
                      {getCurrentInflation('general') > 5 ? (
                        i18n.language === 'es'
                          ? 'Inflación alta detectada. Prioriza renegociar contratos y buscar alternativas más económicas.'
                          : 'Inflation élevée détectée. Priorisez la renégociation de contrats et recherchez des alternatives plus économiques.'
                      ) : (
                        i18n.language === 'es'
                          ? 'Inflación controlada. Buen momento para revisar y optimizar gastos recurrentes.'
                          : 'Inflation maîtrisée. Bon moment pour réviser et optimiser les dépenses récurrentes.'
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* European Comparison */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                {i18n.language === 'es' ? 'Ranking Europeo de Inflación' : 'Classement Européen de l\'Inflation'}
              </CardTitle>
              <CardDescription>
                {i18n.language === 'es' ? 'Comparación con países vecinos' : 'Comparaison avec pays voisins'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={europeanComparison} layout="vercel">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 'dataMax + 1']} />
                  <YAxis dataKey="country" type="category" width={100} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, i18n.language === 'es' ? 'Inflación' : 'Inflation']}
                  />
                  <Bar 
                    dataKey="inflation" 
                    fill="#6366f1"
                    name={i18n.language === 'es' ? 'Tasa de inflación' : 'Taux d\'inflation'}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Data Sources */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-6 border">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              {i18n.language === 'es' ? 'Fuentes de Datos Oficiales' : 'Sources de Données Officielles'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🇫🇷 France - INSEE</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>IPC:</strong> {i18n.language === 'es' ? 'Índice de Precios al Consumo' : 'Indice des Prix à la Consommation'}</li>
                  <li>• <strong>{i18n.language === 'es' ? 'Metodología:' : 'Méthodologie :'}</strong> {i18n.language === 'es' ? 'Cesta familiar tipo' : 'Panier ménage type'}</li>
                  <li>• <strong>{i18n.language === 'es' ? 'Frecuencia:' : 'Fréquence :'}</strong> {i18n.language === 'es' ? 'Mensual' : 'Mensuelle'}</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🇪🇸 España - INE</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>IPC:</strong> {i18n.language === 'es' ? 'Índice de Precios de Consumo' : 'Indice des Prix à la Consommation'}</li>
                  <li>• <strong>{i18n.language === 'es' ? 'Metodología:' : 'Méthodologie :'}</strong> {i18n.language === 'es' ? 'Cesta de la compra familiar' : 'Panier d\'achat familial'}</li>
                  <li>• <strong>{i18n.language === 'es' ? 'Frecuencia:' : 'Fréquence :'}</strong> {i18n.language === 'es' ? 'Mensual' : 'Mensuelle'}</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>{i18n.language === 'es' ? 'Nota metodológica:' : 'Note méthodologique :'}</strong> {i18n.language === 'es'
                  ? 'Los datos presentados son simulaciones basadas en tendencias reales. Para datos oficiales actualizados, consulte directamente INSEE.fr (Francia) o INE.es (España).'
                  : 'Les données présentées sont des simulations basées sur des tendances réelles. Pour des données officielles à jour, consultez directement INSEE.fr (France) ou INE.es (Espagne).'
                }
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};